'use strict';

const MIXED_PATTERNS = Object.freeze([
  'aimedCarrot', 'fanCarrot', 'carrotRain', 'spiralCarrot', 'crossCarrot',
  'ringBurst', 'homingCarrot', 'sweepWall', 'splitCarrot'
]);

class BossPatternEmitter {
  constructor(config, bounds, projectileProfile) {
    this.config = config;
    this.bounds = bounds;
    this.projectileProfile = projectileProfile || null;
    this.timer = 0;
    this.patternIndex = 0;
    this.mixedIndex = 0;
    this.spiralAngle = Math.PI / 2;
    this.lastPatternMeta = null;
  }

  update(dt, boss, player, pool) {
    this.timer -= dt;
    if (this.timer > 0) return 0;
    const burstModifier = Math.max(0.65, Number(this.projectileProfile && this.projectileProfile.burstModifier) || 1);
    this.timer = Math.max(0.08, (Number(this.config.fireInterval) || 1) * burstModifier);
    const patterns = this.config.patterns || ['aimedCarrot'];
    const name = patterns[this.patternIndex % patterns.length] || 'aimedCarrot';
    this.patternIndex += 1;
    return this.emit(name, boss, player, pool);
  }

  emit(name, boss, player, pool) {
    const method = `emit${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    if (typeof this[method] !== 'function') return this.emitAimedCarrot(boss, player, pool);
    return this[method](boss, player, pool);
  }

  origin(boss) {
    return { x: boss.x, y: boss.y + (boss.visualRadius || 70) * 0.45 };
  }

  spawn(pool, options) {
    return pool.spawn({ speed: this.config.carrotSpeed, profile: this.projectileProfile, ...options }) ? 1 : 0;
  }

  emitAimedCarrot(boss, player, pool) {
    const origin = this.origin(boss);
    const heading = Math.atan2(player.y - origin.y, player.x - origin.x);
    this.lastPatternMeta = { type: 'aimedCarrot' };
    return this.spawn(pool, { ...origin, heading });
  }

  emitFanCarrot(boss, player, pool) {
    const origin = this.origin(boss);
    const center = Math.atan2(player.y - origin.y, player.x - origin.x);
    const count = Math.max(1, this.config.volleyCount);
    const modifier = Math.max(0.6, Number(this.projectileProfile && this.projectileProfile.spreadModifier) || 1);
    const spread = Math.min(Math.PI * 0.82, 0.16 * Math.max(1, count - 1) * modifier);
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const offset = count === 1 ? 0 : -spread / 2 + (spread * index) / (count - 1);
      spawned += this.spawn(pool, { ...origin, heading: center + offset, speed: this.config.carrotSpeed * 0.92 });
    }
    this.lastPatternMeta = { type: 'fanCarrot', spread };
    return spawned;
  }

  emitCarrotRain(_boss, _player, pool) {
    const count = Math.max(1, this.config.volleyCount);
    const width = this.bounds.right - this.bounds.left;
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const fraction = (index + 0.5) / count;
      spawned += this.spawn(pool, {
        x: this.bounds.left + width * fraction,
        y: this.bounds.top + 8,
        heading: Math.PI / 2,
        speed: this.config.carrotSpeed * 0.78
      });
    }
    this.lastPatternMeta = { type: 'carrotRain' };
    return spawned;
  }

  emitSpiralCarrot(boss, _player, pool) {
    const origin = this.origin(boss);
    const count = Math.max(1, Math.min(4, this.config.volleyCount));
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      spawned += this.spawn(pool, { ...origin, heading: this.spiralAngle + (Math.PI * 2 * index) / count, speed: this.config.carrotSpeed * 0.82, spin: 2.2 });
    }
    this.spiralAngle = (this.spiralAngle + 0.42) % (Math.PI * 2);
    this.lastPatternMeta = { type: 'spiralCarrot' };
    return spawned;
  }

  emitCrossCarrot(_boss, player, pool) {
    const count = Math.max(2, this.config.volleyCount);
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const fromLeft = index % 2 === 0;
      const x = fromLeft ? this.bounds.left + 8 : this.bounds.right - 8;
      const y = this.bounds.top + 65 + (index % Math.ceil(count / 2)) * 34;
      const heading = Math.atan2(player.y - y, player.x - x);
      spawned += this.spawn(pool, { x, y, heading, speed: this.config.carrotSpeed * 0.86 });
    }
    this.lastPatternMeta = { type: 'crossCarrot' };
    return spawned;
  }

  emitRingBurst(boss, player, pool) {
    const origin = this.origin(boss);
    const gap = (48 * Math.PI) / 180;
    const gapCenter = Math.atan2(player.y - origin.y, player.x - origin.x);
    const count = Math.max(6, this.config.volleyCount);
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const heading = (Math.PI * 2 * index) / count;
      const difference = Math.abs(Math.atan2(Math.sin(heading - gapCenter), Math.cos(heading - gapCenter)));
      if (difference < gap / 2) continue;
      spawned += this.spawn(pool, { ...origin, heading, speed: this.config.carrotSpeed * 0.72 });
    }
    this.lastPatternMeta = { type: 'ringBurst', safeGapRadians: gap, gapCenter };
    return spawned;
  }

  emitHomingCarrot(boss, player, pool) {
    const origin = this.origin(boss);
    const center = Math.atan2(player.y - origin.y, player.x - origin.x);
    const count = Math.max(1, this.config.volleyCount);
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const offset = (index - (count - 1) / 2) * 0.11;
      spawned += this.spawn(pool, { ...origin, heading: center + offset, speed: this.config.carrotSpeed * 0.68, motionKey: 'homing', turnRate: Math.max(1.25, Number(this.projectileProfile && this.projectileProfile.turnRate) || 0) });
    }
    this.lastPatternMeta = { type: 'homingCarrot' };
    return spawned;
  }

  emitSweepWall(_boss, player, pool) {
    const count = Math.max(5, this.config.volleyCount);
    const width = this.bounds.right - this.bounds.left;
    const safeWidth = Math.max(72, (player.visualRadius || 32) * 2.8);
    const gapCenter = Math.max(this.bounds.left + safeWidth / 2, Math.min(this.bounds.right - safeWidth / 2, player.x));
    const gapLeft = gapCenter - safeWidth / 2;
    const gapRight = gapCenter + safeWidth / 2;
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const x = this.bounds.left + width * ((index + 0.5) / count);
      if (x >= gapLeft && x <= gapRight) continue;
      spawned += this.spawn(pool, { x, y: this.bounds.top + 8, heading: Math.PI / 2, speed: this.config.carrotSpeed * 0.74 });
    }
    this.lastPatternMeta = { type: 'sweepWall', gapLeft, gapRight, safeWidth };
    return spawned;
  }

  emitSplitCarrot(boss, player, pool) {
    const origin = this.origin(boss);
    const center = Math.atan2(player.y - origin.y, player.x - origin.x);
    const count = Math.max(1, Math.min(3, this.config.volleyCount));
    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      spawned += this.spawn(pool, {
        ...origin,
        heading: center + (index - (count - 1) / 2) * 0.16,
        speed: this.config.carrotSpeed * 0.76,
        splitAfter: 0.82,
        splitCount: Math.max(3, Math.min(7, this.config.volleyCount))
      });
    }
    this.lastPatternMeta = { type: 'splitCarrot' };
    return spawned;
  }

  emitMixedCarrot(boss, player, pool) {
    const unlockedCount = Math.max(1, Math.min(9, Math.floor((this.config.level || 90) / 10)));
    const name = MIXED_PATTERNS[this.mixedIndex % unlockedCount];
    this.mixedIndex += 1;
    const result = this.emit(name, boss, player, pool);
    this.lastPatternMeta = { ...(this.lastPatternMeta || {}), mixed: true, sourcePattern: name };
    return result;
  }
}

module.exports = BossPatternEmitter;
